import { useRef } from "react";

import { Button } from "./Button";
import { AVATAR_ACCEPT } from "~/lib/avatar";

type AvatarUploadFieldProps = {
  /** Current avatar src (served `/media` path or legacy URL), or null. */
  avatarUrl: string | null;
  /** Initials fallback shown inside the avatar circle when no photo is set. */
  initials: string;
  /** True while an avatar upload/remove submission is in flight. */
  busy: boolean;
  /** Resolved error copy for the last avatar submission, or null. */
  error?: string | null;
  /** Label for the picker button when no photo is set yet. */
  uploadLabel: string;
  /** Label for the picker button when a photo already exists. */
  changeLabel: string;
  /** Label for the remove action (only shown when a photo exists). */
  removeLabel: string;
  /** Helper note under the controls (formats / fallback explanation). */
  note: string;
  /** The wait, announced to assistive tech while a submission is pending (T972: the picker keeps its visible label). */
  uploadingLabel: string;
  /** Called with the chosen File once the member picks one. */
  onFileSelected: (file: File) => void;
  /** Called when the member removes the current photo. */
  onRemove: () => void;
};

/**
 * T278 — member avatar control: avatar circle (photo or initials) plus a file
 * picker and a remove action. Pure/presentational — it owns the raw file input
 * (a UI-primitive responsibility, R16) but performs no fetching: the consumer
 * submits via `onFileSelected` / `onRemove`.
 */
export function AvatarUploadField({
  avatarUrl,
  initials,
  busy,
  error,
  uploadLabel,
  changeLabel,
  removeLabel,
  note,
  uploadingLabel,
  onFileSelected,
  onRemove,
}: AvatarUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-wrap items-center gap-[var(--tfc-gap-lg)]">
      <div
        className="pf-id-avatar"
        style={{ width: 84, height: 84, fontSize: 30, boxShadow: "0 0 0 1px var(--color-borderLight)" }}
      >
        {avatarUrl ? <img src={avatarUrl} alt="" /> : initials}
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={AVATAR_ACCEPT}
          aria-label={avatarUrl ? changeLabel : uploadLabel}
          tabIndex={-1}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset so re-selecting the same file still fires onChange.
            e.target.value = "";
            if (file) onFileSelected(file);
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            type="button"
            isLoading={busy}
            busyLabel={uploadingLabel}
            onClick={() => inputRef.current?.click()}
          >
            {avatarUrl ? changeLabel : uploadLabel}
          </Button>
          {avatarUrl ? (
            <Button variant="text" className="muted" type="button" disabled={busy} onClick={onRemove}>
              {removeLabel}
            </Button>
          ) : null}
        </div>
        {error ? (
          <p className="pf-block-note text-danger" role="alert" style={{ margin: 0 }}>
            {error}
          </p>
        ) : (
          <p className="pf-block-note" style={{ margin: 0 }}>
            {note}
          </p>
        )}
      </div>
    </div>
  );
}
